import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import MonacoEditor, { OnMount, BeforeMount } from '@monaco-editor/react';
import { editor } from 'monaco-editor';
import type * as monacoEditor from 'monaco-editor';

import { READONLY_CLASS_NAME, MAIN_CLASS_NAME, DEFAULT_EDITOR_STATUS } from './const';
import { setupContentObservation } from './utils';
import { CodeEditorStatus } from './types';

import './index.less';

export interface ICodeEditorProps {
  value?: string;
  defaultValue?: string;
  isFullscreen?: boolean;
  readOnly?: boolean;
  codeLanguage?: string;
  codeUrl?: string;
  options?: monacoEditor.editor.IStandaloneEditorConstructionOptions;
  onBlur?: () => void;
  onChange?: (value?: string) => void;
  onValidate?: (errors: string[], status: CodeEditorStatus, value?: string) => void;
  beforeEditorMount?: BeforeMount;
  onEditorMount?: OnMount;
}

const CodeEditor: React.FC<ICodeEditorProps> = ({
  value,
  defaultValue,
  isFullscreen,
  readOnly = false,
  codeLanguage,
  codeUrl,
  options,
  onBlur,
  onChange,
  onValidate,
  beforeEditorMount,
  onEditorMount,
}) => {
  const { t } = useTranslation();
  const editorRef = useRef<monacoEditor.editor.IStandaloneCodeEditor | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !editorRef.current) return;

    const observer = new ResizeObserver(() => {
      if (editorRef.current) {
        editorRef.current.layout();
      }
    });

    observer.observe(container);
    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.setScrollPosition({ scrollTop: 0 });
    }
  }, [isFullscreen]);

  const onMonacoValidate = (markers: editor.IMarker[], content?: string) => {
    if (!onValidate) {
      return;
    }

    const errors = markers.map((marker) => marker.message);
    const status = content
      ? errors.length
        ? CodeEditorStatus.Error
        : CodeEditorStatus.Success
      : DEFAULT_EDITOR_STATUS;
    onValidate(errors, status, content);
  };

  const onMonacoMount: OnMount = (editor, monaco) => {
    editorRef.current = editor;
    onEditorMount?.(editor, monaco);
    setupContentObservation(editor, monaco, onMonacoValidate, onMonacoValidate);
  };

  const classes = [MAIN_CLASS_NAME];
  readOnly && classes.push(READONLY_CLASS_NAME);

  return (
    <div ref={containerRef} className={classes.join(' ')} onBlur={onBlur}>
      <MonacoEditor
        beforeMount={beforeEditorMount}
        defaultValue={defaultValue}
        height="100%"
        language={codeLanguage}
        loading={t('Common: Code editor: Loading')}
        options={{ ...options, readOnly: readOnly }}
        path={codeUrl}
        value={value}
        onChange={onChange}
        onMount={onMonacoMount}
        onValidate={(markers) => onMonacoValidate(markers, value)}
      />
    </div>
  );
};

export default CodeEditor;
